/**
 * services/api.js — Phase 3  (drop-in replacement for Phase 2)
 *
 * Compatibility with Phase 2:
 *  ✓ accessToken stays in-memory (not sessionStorage) — same as Phase 2
 *  ✓ localStorage key stays 'hrms_refresh_token' — existing sessions survive
 *  ✓ API response envelope stays data.data.* — matches Spring Boot ApiResponse<T>
 *  ✓ Redirect target stays '/' — same as Phase 2
 *
 * New in Phase 3:
 *  + tokenStore.setExpiry / getExpiry / isExpiringSoon — enables proactive refresh
 *  + REQUEST interceptor: proactively refreshes 60 s before expiry (no 401 in normal flow)
 *  + Race-safe queue — concurrent requests during refresh are replayed, not dropped
 *  + clearAll() also clears the stored expiry
 */

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

// ── Token storage ─────────────────────────────────────────────────────────────
// Access token: in-memory (same as Phase 2 — avoids XSS via sessionStorage)
// Refresh token: localStorage key unchanged ('hrms_refresh_token')
let _accessToken = null;

export const tokenStore = {
  // Access (in-memory — unchanged from Phase 2)
  getAccess:    ()    => _accessToken,
  setAccess:    (t)   => { _accessToken = t; },
  clearAccess:  ()    => { _accessToken = null; },

  // Refresh (localStorage key unchanged from Phase 2)
  getRefresh:   ()    => localStorage.getItem('hrms_refresh_token'),
  setRefresh:   (t)   => localStorage.setItem('hrms_refresh_token', t),
  clearRefresh: ()    => localStorage.removeItem('hrms_refresh_token'),

  // Expiry — NEW in Phase 3 (stored in localStorage alongside refresh token)
  getExpiry:    ()    => Number(localStorage.getItem('hrms_token_expiry') || 0),
  setExpiry:    (ms)  => localStorage.setItem('hrms_token_expiry', String(ms)),
  clearExpiry:  ()    => localStorage.removeItem('hrms_token_expiry'),

  /** True if the access token expires within the next 60 seconds */
  isExpiringSoon: () => {
    const expiry = Number(localStorage.getItem('hrms_token_expiry') || 0);
    return expiry > 0 && Date.now() >= expiry - 60_000;
  },

  clearAll() {
    this.clearAccess();
    this.clearRefresh();
    this.clearExpiry();
  },
};

// ── Axios instance ────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json','ngrok-skip-browser-warning': 'true' }
});

// ── Refresh helper (bare axios — bypasses our own interceptors) ───────────────
let _isRefreshing = false;
let _pendingQueue = []; // [{ resolve, reject }]

function flushQueue(token) {
  _pendingQueue.forEach(({ resolve }) => resolve(token));
  _pendingQueue = [];
}
function drainQueue(err) {
  _pendingQueue.forEach(({ reject }) => reject(err));
  _pendingQueue = [];
}

async function doRefresh() {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) throw new Error('No refresh token');

  // Use bare axios — avoids triggering this interceptor again
  // Backend returns ApiResponse<AuthDto>: { data: { accessToken, refreshToken, expiresIn } }
  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
  const payload = data.data; // unwrap Spring Boot ApiResponse envelope

  tokenStore.setAccess(payload.accessToken);
  tokenStore.setRefresh(payload.refreshToken);
  tokenStore.setExpiry(Date.now() + (payload.expiresIn ?? 900) * 1_000);

  return payload.accessToken;
}

// ── REQUEST interceptor — attach token + proactive refresh ───────────────────
api.interceptors.request.use(
  async (config) => {
    // Skip auth header for login / refresh endpoints
    const noAuth = ['/auth/login', '/auth/refresh'];
    if (noAuth.some((p) => config.url?.includes(p))) return config;

    // Proactively refresh if token is about to expire
    if (tokenStore.getRefresh() && tokenStore.isExpiringSoon() && !_isRefreshing) {
      _isRefreshing = true;
      try {
        const newToken = await doRefresh();
        flushQueue(newToken);
        config.headers['Authorization'] = `Bearer ${newToken}`;
        return config;
      } catch {
        // Fall through — response interceptor will handle the resulting 401
      } finally {
        _isRefreshing = false;
      }
    }

    const token = tokenStore.getAccess();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (err) => Promise.reject(err),
);

// ── RESPONSE interceptor — reactive 401 handling ─────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Let /auth/login and /auth/refresh failures propagate untouched —
    // AuthContext.login already handles their errors (bad creds, locked
    // account, refresh failure). Treating their 401s as "session expired"
    // wipes tokenStore and force-navigates away mid-login.
    const noAuth = ['/auth/login', '/auth/refresh'];
    if (noAuth.some((p) => originalRequest?.url?.includes(p))) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }
    originalRequest._retried = true;

    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) {
      tokenStore.clearAll();
      window.location.href = '/';
      return Promise.reject(error);
    }

    if (_isRefreshing) {
      // Queue while another refresh is in flight
      return new Promise((resolve, reject) => {
        _pendingQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    _isRefreshing = true;
    try {
      const newToken = await doRefresh();
      flushQueue(newToken);
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      drainQueue(refreshError);
      tokenStore.clearAll();
      window.location.href = '/';
      return Promise.reject(refreshError);
    } finally {
      _isRefreshing = false;
    }
  },
);

export default api;
